<script setup lang="ts">
import { openGlobalUserProfile } from '@/components/helper'
import { ILeaderboard, TimeFrame } from '@/models/Leaderboard'
import { UIDivider, UITextMdMedium, UITextMdNormal } from '@gohighlevel/ghl-ui'
import { PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import LeaderboardTableRow from '../LeaderboardTableRow.vue'

const props = defineProps({
  timeframe: {
    type: String as PropType<TimeFrame>,
    default: TimeFrame.THIRTY_DAY,
  },
  isInfoCard: {
    type: Boolean,
    default: false,
  },
  currentUserRank: {
    type: Object as PropType<ILeaderboard | undefined>,
    required: false,
  },
  leaderboardData: {
    type: Object as PropType<ILeaderboard[]>,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
})
const router = useRouter()
const { t } = useI18n()

function handleBtnClick() {
  router.push({ name: 'leaderboard' })
}

const titleMap = {
  '7D': t('leaderboard.leaderboard7d'),
  '30D': t('leaderboard.leaderboard30d'),
  ALL_TIME: t('leaderboard.leaderboardAlltime'),
}

const title = titleMap[props.timeframe]
</script>

<template>
  <div class="flex w-full flex-col">
    <div
      :class="[
        props.isInfoCard ? 'info-card-container' : '',
        'basis-1/3-gap-4',
        props.isInfoCard ? 'rounded-tl-xl rounded-tr-xl' : 'rounded-xl',
        'bg-communities-background',
        props.isInfoCard ? 'py-4' : 'pb-4 pt-4',
      ]"
    >
      <UITextMdMedium class="px-5 font-semibold text-communities-font-primary">
        {{ title }}
      </UITextMdMedium>
      <UIDivider />
      <div v-if="!props.isLoading || !props.isInfoCard">
        <div
          v-if="props.leaderboardData?.length > 0"
          class="flex flex-col gap-1"
        >
          <LeaderboardTableRow
            v-for="(rowItem, index) in props.leaderboardData"
            :key="index"
            :mobile-view="false"
            :row-item="rowItem"
            @click="openGlobalUserProfile(rowItem.user.slug, router)"
          />
          <div v-if="props.currentUserRank" class="flex flex-col">
            <UITextMdMedium class="px-5 pt-5">
              {{ t('leaderboard.yourRank') }}</UITextMdMedium
            >
            <UIDivider />
            <LeaderboardTableRow
              :key="10"
              :mobile-view="false"
              :row-item="props.currentUserRank"
              @click="
                openGlobalUserProfile(props.currentUserRank.user.slug, router)
              "
            />
          </div>
        </div>
        <div v-else class="px-5">
          <UITextMdNormal class="text-communities-font-secondary">
            {{ t('leaderboard.noActivityToShow') }}
          </UITextMdNormal>
        </div>
      </div>
    </div>
    <div
      v-if="isInfoCard"
      class="see-all-leaderboard-btn flex w-full cursor-pointer items-center justify-center bg-communities-background py-4"
    >
      <UITextMdNormal
        class="text-communities-tertiary hover:underline"
        @click.stop="handleBtnClick"
      >
        {{ t('leaderboard.seeAllLeaderboards') }}
      </UITextMdNormal>
    </div>
  </div>
</template>
<style scoped>
.n-divider:not(.n-divider--vertical) {
  margin-top: 16px !important;
  margin-bottom: 14px !important;
}

.info-card-container {
  width: 100%;
  border-right: var(--border-style) var(--border);
  border-bottom: var(--border-style) var(--border);
  border-left: var(--border-style) var(--border);
}
.see-all-leaderboard-btn {
  border-radius: 0px 0px 10px 10px;
  border-right: var(--border-style) var(--border);
  border-bottom: var(--border-style) var(--border);
  border-left: var(--border-style) var(--border);
}
</style>
